// src/pages/campaigns/CampaignsManagementPage.jsx
import React, { useState, useEffect } from 'react';
import { get, del } from '../../helpers/apiHelper';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import Swal from 'sweetalert2';
import {
    FaPlus, FaEye, FaEdit, FaTrash, FaFilter, FaSearch,
    FaGamepad, FaUsers, FaGift, FaEnvelope, FaWhatsapp,
    FaCalendar, FaChartLine, FaBullseye
} from 'react-icons/fa';
import CampaignDetailModal from '../../components/campaigns/CampaignDetailModal';

function CampaignsManagementPage() {
    const [campaigns, setCampaigns] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBrand, setSelectedBrand] = useState('all');
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const userId = user.userId;

    useEffect(() => {
        if (userId) {
            fetchBrands();
        }
    }, [userId]);

    useEffect(() => {
        if (brands.length > 0) {
            fetchAllCampaigns();
        }
    }, [brands]);

    const fetchBrands = async () => {
        try {
            const response = await get(`marcas?usuario=${userId}`);
            setBrands(response);
        } catch (error) {
            console.error("Error al obtener marcas:", error);
            Swal.fire("Error", error.message, "error");
        }
    };

    const fetchAllCampaigns = async () => {
        setLoading(true);
        try {
            const allCampaigns = [];

            // Obtener campañas de game-loyalty
            try {
                const brandIds = brands.map((brand) => brand._id).join(",");
                const gameLoyaltyCampaigns = await get(`game-loyalty?marcaId=${brandIds}`);
                allCampaigns.push(...gameLoyaltyCampaigns.map(campaign => ({
                    ...campaign,
                    tipo: 'game-loyalty',
                    tipoDisplay: 'Juego y Fidelización',
                    icon: <FaGamepad className="text-purple-500" />,
                    nombre: campaign.nombre,
                    descripcion: campaign.descripcion,
                    estado: campaign.estado || 'DRAFT',
                    marcaId: campaign.marcaId,
                    fechaCreacion: campaign.fechaCreacion || campaign.createdAt
                })));
            } catch (error) {
                console.warn("Error obteniendo campañas game-loyalty:", error);
            }

            // Obtener campañas de referral
            try {
                const referralCampaigns = await get(`referral-campaign`);
                allCampaigns.push(...referralCampaigns.map(campaign => ({
                    ...campaign,
                    tipo: 'referral',
                    tipoDisplay: 'Referidos',
                    icon: <FaUsers className="text-blue-500" />,
                    nombre: campaign.nombre,
                    descripcion: campaign.descripcion,
                    estado: campaign.estado || 'DRAFT',
                    marcaId: campaign.marcaId,
                    fechaCreacion: campaign.fechaCreacion || campaign.createdAt
                })));
            } catch (error) {
                console.warn("Error obteniendo campañas referral:", error);
            }

            // Obtener campañas de marketing IA (si existen)
            try {
                console.log("Obteniendo campañas de IA desde el backend...");

                // Usar el endpoint real que ya está implementado
                const aiCampaigns = await get(`agents/marketing/campaigns`);

                console.log('Campañas de IA encontradas:', aiCampaigns);

                allCampaigns.push(...aiCampaigns.map(campaign => ({
                    ...campaign,
                    tipo: 'ai-marketing',
                    tipoDisplay: 'IA Marketing',
                    icon: <FaBullseye className="text-green-500" />,
                    nombre: campaign.name || campaign.nombre,
                    descripcion: campaign.description || campaign.descripcion,
                    estado: campaign.status || campaign.estado || 'DRAFT',
                    marcaId: campaign.marca || campaign.marcaId,
                    fechaCreacion: campaign.createdAt || campaign.fechaCreacion,
                    tipoCampaign: campaign.type || campaign.tipo
                })));

            } catch (error) {
                console.warn("Error obteniendo campañas IA:", error);
                // Si falla, intentar endpoint general de campañas
                try {
                    console.log("Intentando endpoint general de campañas...");
                    const generalCampaigns = await get(`campaigns`);
                    const aiCampaigns = generalCampaigns.filter(campaign =>
                        campaign.type === 'SOCIAL_MEDIA' ||
                        campaign.type === 'LOYALTY' ||
                        campaign.type === 'EMAIL' ||
                        campaign.type === 'SMS' ||
                        campaign.type === 'PUSH' ||
                        campaign.type === 'WHATSAPP'
                    );

                    console.log('Campañas de IA filtradas desde endpoint general:', aiCampaigns);

                    allCampaigns.push(...aiCampaigns.map(campaign => ({
                        ...campaign,
                        tipo: 'ai-marketing',
                        tipoDisplay: 'IA Marketing',
                        icon: <FaBullseye className="text-green-500" />,
                        nombre: campaign.name || campaign.nombre,
                        descripcion: campaign.description || campaign.descripcion,
                        estado: campaign.status || campaign.estado || 'DRAFT',
                        marcaId: campaign.marca || campaign.marcaId,
                        fechaCreacion: campaign.createdAt || campaign.fechaCreacion,
                        tipoCampaign: campaign.type || campaign.tipo
                    })));
                } catch (innerError) {
                    console.warn("No se pudieron obtener campañas de IA:", innerError);
                }
            }

            setCampaigns(allCampaigns);
            console.log('Todas las campañas obtenidas:', allCampaigns);
        } catch (error) {
            console.error("Error al obtener campañas:", error);
            Swal.fire("Error", "No se pudieron cargar las campañas", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (campaign) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    let endpoint = '';
                    switch (campaign.tipo) {
                        case 'game-loyalty':
                            endpoint = `game-loyalty/${campaign._id}`;
                            break;
                        case 'referral':
                            endpoint = `referral-campaign/${campaign._id}`;
                            break;
                        case 'ai-marketing':
                            endpoint = `agents/marketing/campaigns/${campaign._id}`;
                            break;
                        default:
                            throw new Error('Tipo de campaña no soportado');
                    }

                    await del(endpoint);
                    Swal.fire('Eliminado', 'La campaña ha sido eliminada correctamente', 'success');
                    fetchAllCampaigns();
                } catch (error) {
                    console.error('Error al eliminar campaña:', error);
                    Swal.fire('Error', error.message, 'error');
                }
            }
        });
    };

    const handleViewDetails = (campaign) => {
        setSelectedCampaign(campaign);
        setShowDetailModal(true);
    };

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedCampaign(null);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'activa':
            case 'active':
                return 'bg-green-100 text-green-800';
            case 'pendiente':
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'pausada':
            case 'paused':
                return 'bg-orange-100 text-orange-800';
            case 'finalizada':
            case 'finished':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-blue-100 text-blue-800';
        }
    };

    const getStatusText = (status) => {
        switch (status?.toLowerCase()) {
            case 'activa':
            case 'active':
                return 'Activa';
            case 'pendiente':
            case 'pending':
                return 'Pendiente';
            case 'pausada':
            case 'paused':
                return 'Pausada';
            case 'finalizada':
            case 'finished':
                return 'Finalizada';
            default:
                return 'Borrador';
        }
    };

    const filteredCampaigns = campaigns.filter(campaign => {
        const matchesType = filterType === 'all' || campaign.tipo === filterType;
        const matchesSearch = (campaign.nombre || campaign.name)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (campaign.descripcion || campaign.description)?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesBrand = selectedBrand === 'all' || campaign.marcaId === selectedBrand || campaign.marca === selectedBrand;

        return matchesType && matchesSearch && matchesBrand;
    });

    const campaignTypes = [
        { value: 'all', label: 'Todas', icon: <FaChartLine /> },
        { value: 'game-loyalty', label: 'Juego y Fidelización', icon: <FaGamepad /> },
        { value: 'referral', label: 'Referidos', icon: <FaUsers /> },
        { value: 'ai-marketing', label: 'IA Marketing', icon: <FaBullseye /> }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando campañas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        Gestión de Campañas
                    </h1>
                    <p className="text-gray-600">
                        Administra todas tus campañas de marketing desde un solo lugar
                    </p>
                </div>

                {/* Filtros y búsqueda */}
                <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Búsqueda */}
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar campañas..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Filtro por tipo */}
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {campaignTypes.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>

                        {/* Filtro por marca */}
                        <select
                            value={selectedBrand}
                            onChange={(e) => setSelectedBrand(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">Todas las marcas</option>
                            {brands.map(brand => (
                                <option key={brand._id} value={brand._id}>
                                    {brand.nombre || brand.displayName}
                                </option>
                            ))}
                        </select>

                        {/* Botón crear nueva campaña */}
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                        >
                            <FaPlus className="mr-2" />
                            Nueva Campaña
                        </button>
                    </div>
                </div>

                {/* Estadísticas rápidas */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaChartLine className="text-blue-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Total Campañas</p>
                                <p className="text-2xl font-bold text-gray-800">{campaigns.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaGamepad className="text-purple-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Juego y Fidelización</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {campaigns.filter(c => c.tipo === 'game-loyalty').length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaUsers className="text-blue-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Referidos</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {campaigns.filter(c => c.tipo === 'referral').length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaBullseye className="text-green-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">IA Marketing</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {campaigns.filter(c => c.tipo === 'ai-marketing').length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lista de campañas */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {filteredCampaigns.length === 0 ? (
                        <div className="p-8 text-center">
                            <FaChartLine className="text-4xl text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-600 mb-2">
                                No se encontraron campañas
                            </h3>
                            <p className="text-gray-500 mb-4">
                                {searchTerm || filterType !== 'all' || selectedBrand !== 'all'
                                    ? 'Intenta ajustar los filtros de búsqueda'
                                    : 'Crea tu primera campaña para comenzar'
                                }
                            </p>
                            {!searchTerm && filterType === 'all' && selectedBrand === 'all' && (
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                                >
                                    Crear Primera Campaña
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Campaña
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Tipo
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Estado
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Participación
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Fecha
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredCampaigns.map((campaign) => (
                                        <tr key={`${campaign.tipo}-${campaign._id}`} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center">
                                                        {campaign.icon}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {campaign.nombre}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {campaign.descripcion?.substring(0, 50)}...
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    {campaign.tipoDisplay}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.estado)}`}>
                                                    {getStatusText(campaign.estado)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {campaign.participations ? campaign.participations.length : 0} / {campaign.totalParticipations || '∞'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {campaign.fechaCreacion ? new Date(campaign.fechaCreacion).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleViewDetails(campaign)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Ver detalles"
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(campaign)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="Eliminar"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <CampaignDetailModal
                isOpen={showDetailModal}
                campaign={selectedCampaign}
                onClose={handleCloseDetailModal}
                onDelete={handleDelete}
            />
        </div>
    );
}

export default CampaignsManagementPage; 